import { supabase } from '../lib/supabase';
import { UserRole } from '../config/constants';

export interface UserProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: UserRole;
  company_id: string;
  company_name: string;
  company_logo_url: string | null;
  company_slug: string;
  company_contact_email: string | null;
  created_at: string;
}

export interface UpdateProfileData {
  first_name: string;
  last_name: string;
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    // Étape 1 : récupérer l'utilisateur (sans join PostgREST pour éviter les problèmes de schema cache)
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, email, first_name, last_name, role, company_id, created_at')
      .eq('id', userId)
      .maybeSingle();

    if (userError || !userData) {
      console.error('Error fetching user:', userError);
      return null;
    }

    // Étape 2 : récupérer la company séparément
    const { data: companyData } = await supabase
      .from('companies')
      .select('name, logo_url, slug, contact_email')
      .eq('id', userData.company_id)
      .maybeSingle();

    return {
      id: userData.id,
      email: userData.email,
      first_name: userData.first_name || null,
      last_name: userData.last_name || null,
      role: userData.role as UserRole,
      company_id: userData.company_id,
      company_name: companyData?.name ?? '',
      company_logo_url: companyData?.logo_url ?? null,
      company_slug: companyData?.slug ?? '',
      company_contact_email: companyData?.contact_email ?? null,
      created_at: userData.created_at,
    };
  } catch (error) {
    console.error('Unexpected error fetching user profile:', error);
    return null;
  }
}

export async function updateUserProfile(
  userId: string,
  data: Partial<UpdateProfileData>
): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({
      first_name: data.first_name,
      last_name: data.last_name,
    })
    .eq('id', userId);

  if (error) throw new Error('Failed to update profile');
}

export async function updateUserEmail(userId: string, newEmail: string): Promise<void> {
  const { error: authError } = await supabase.auth.updateUser({ email: newEmail });
  if (authError) throw new Error(authError.message);

  const { error: dbError } = await supabase
    .from('users')
    .update({ email: newEmail })
    .eq('id', userId);

  if (dbError) throw new Error('Failed to update email in profile');
}
